import { createClient } from '@supabase/supabase-js';

// Get Supabase URL and anon key from environment variables or runtime config
const getSupabaseUrl = () => {
  return window.REACT_APP_SUPABASE_URL || process.env.REACT_APP_SUPABASE_URL;
};

const getSupabaseAnonKey = () => {
  return window.REACT_APP_SUPABASE_ANON_KEY || process.env.REACT_APP_SUPABASE_ANON_KEY;
};

const getSupabaseServiceKey = () => {
  return window.REACT_APP_SUPABASE_SERVICE_KEY || process.env.REACT_APP_SUPABASE_SERVICE_KEY;
};

// Create a Supabase client
const supabaseUrl = getSupabaseUrl();
const supabaseAnonKey = getSupabaseAnonKey();
const supabaseServiceKey = getSupabaseServiceKey();

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase URL or anon key not found. Make sure they are set in environment variables or runtime config.');
}

// Log the Supabase configuration (without exposing full keys)
console.log('Supabase Configuration:');
console.log('URL:', supabaseUrl);
console.log('Anon Key Available:', !!supabaseAnonKey);
console.log('Service Key Available:', !!supabaseServiceKey);

// Create the client with specific options
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: {
      getItem: (key) => {
        try {
          const value = localStorage.getItem(key);
          console.log(`Auth storage: Getting ${key}`, value ? 'Found' : 'Not found');
          return value;
        } catch (error) {
          console.error(`Auth storage: Error getting ${key}:`, error);
          return null;
        }
      },
      setItem: (key, value) => {
        try {
          console.log(`Auth storage: Setting ${key}`);
          localStorage.setItem(key, value);
        } catch (error) {
          console.error(`Auth storage: Error setting ${key}:`, error);
        }
      },
      removeItem: (key) => {
        try {
          console.log(`Auth storage: Removing ${key}`);
          localStorage.removeItem(key);
        } catch (error) {
          console.error(`Auth storage: Error removing ${key}:`, error);
        }
      }
    },
    storageKey: 'supabase_auth_token',
    debug: true
  }
});

// Create admin client if service key is available
export const supabaseAdmin = supabaseServiceKey ? createClient(supabaseUrl, supabaseServiceKey) : null;

// Log client creation
console.log('Supabase client created:', !!supabase);
console.log('Supabase admin client created:', !!supabaseAdmin);

// Authentication helpers
export const auth = {
  signUp: async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback`
      }
    });
    return { user: data.user, error };
  },
  
  signIn: async (email, password) => {
    try {
      console.log('Supabase API: Attempting signIn with email');
      
      // Perform the sign in
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('Supabase API: signIn error:', error);
        return { user: null, error };
      }
      
      console.log('Supabase API: signIn successful, user:', data.user?.id);
      return { user: data.user, error: null };
    } catch (err) {
      console.error('Supabase API: Unexpected error in signIn:', err);
      return { user: null, error: err };
    }
  },
  
  signOut: async () => {
    try {
      console.log('Supabase API: Attempting signOut');
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('Supabase API: Error during signOut:', error);
        return { error };
      }
      
      console.log('Supabase API: signOut completed');
      return { error: null };
    } catch (err) {
      console.error('Supabase API: Unexpected error during signOut:', err);
      return { error: err };
    }
  },
  
  getCurrentUser: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  },
  
  onAuthStateChange: (callback) => {
    return supabase.auth.onAuthStateChange(callback);
  },
  
  // Debug utility to check session state
  debugSession: async () => {
    try {
      console.log('Checking Supabase session state...');
      
      // Get current session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Error getting session:', sessionError);
        return { error: sessionError };
      }
      
      console.log('Current session:', sessionData);
      
      // Get current user
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('Error getting user:', userError);
        return { error: userError };
      }
      
      console.log('Current user:', userData);
      
      // Check localStorage for Supabase items
      console.log('Checking localStorage for Supabase items:');
      const supabaseItems = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('supabase.') || key.startsWith('sb-'))) {
          supabaseItems.push(key);
        }
      }
      
      console.log('Supabase localStorage items:', supabaseItems);
      
      return { 
        session: sessionData,
        user: userData,
        localStorage: supabaseItems
      };
    } catch (err) {
      console.error('Error in debugSession:', err);
      return { error: err };
    }
  }
};

// Database helpers
export const db = {
  // Users (unified table for buyers, sellers, admins, and support)
  getUsers: async () => {
    const { data, error } = await supabase
      .from('users')
      .select('*');
    return { data, error };
  },
  
  getUserByAuthId: async (authId) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', authId)
      .single();
    return { data, error };
  },
  
  getUserById: async (id) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();
    return { data, error };
  },
  
  createUser: async (userData) => {
    const { data, error } = await supabase
      .from('users')
      .insert([userData])
      .select();
    return { data, error };
  },
  
  updateUser: async (id, userData) => {
    const { data, error } = await supabase
      .from('users')
      .update(userData)
      .eq('id', id)
      .select();
    return { data, error };
  },
  
  // Legacy methods for backward compatibility
  getBuyerByUserId: async (userId) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', userId)
      .eq('user_type', 'buyer')
      .single();
    return { data, error };
  },
  
  getSellerByUserId: async (userId) => {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('auth_id', userId)
      .eq('user_type', 'seller')
      .single();
    return { data, error };
  },
  
  createBuyer: async (buyerData) => {
    const userData = {
      ...buyerData,
      user_type: 'buyer'
    };
    return await db.createUser(userData);
  },
  
  updateBuyer: async (id, buyerData) => {
    return await db.updateUser(id, buyerData);
  },
  
  createSeller: async (sellerData) => {
    const userData = {
      ...sellerData,
      user_type: 'seller'
    };
    return await db.createUser(userData);
  },
  
  updateSeller: async (id, sellerData) => {
    return await db.updateUser(id, sellerData);
  },
  
  // Leads
  getLeads: async () => {
    const { data, error } = await supabase
      .from('leads')
      .select('*')
      .order('created_at', { ascending: false });
    return { data, error };
  },
  
  createLead: async (leadData) => {
    const { data, error } = await supabase
      .from('leads')
      .insert([leadData])
      .select();
    return { data, error };
  },
  
  updateLeadStatus: async (id, status) => {
    const { data, error } = await supabase
      .from('leads')
      .update({ status, updated_at: new Date() })
      .eq('id', id)
      .select();
    return { data, error };
  },
  
  // Get leads for a specific user
  getUserLeads: async (userId, userType) => {
    let query = supabase
      .from('leads')
      .select('*');
      
    if (userType === 'buyer') {
      query = query.eq('user_id', userId).eq('lead_owner_type', 'buyer');
    } else if (userType === 'seller') {
      query = query.eq('assigned_to_id', userId).eq('lead_owner_type', 'seller');
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    return { data, error };
  }
}; 